import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, AlertTriangle, RefreshCw, FileText, CheckCircle2,
  Clock, ExternalLink, Loader2, ChevronLeft, Building2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function AdminCompliance() {
  const [tab, setTab] = useState('digest');
  const [digest, setDigest] = useState(null);
  const [items, setItems] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [subprocessors, setSubprocessors] = useState({ items: [], tracked: [] });
  const [loading, setLoading] = useState(true);
  const [runningPulse, setRunningPulse] = useState(false);
  const [runningScan, setRunningScan] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [d, i, p, s] = await Promise.all([
        axios.get(`${API}/api/admin/compliance/digest`, { headers: authHeader() }),
        axios.get(`${API}/api/admin/compliance/items`, { headers: authHeader() }),
        axios.get(`${API}/api/admin/compliance/policies`, { headers: authHeader() }),
        axios.get(`${API}/api/admin/compliance/subprocessors`, { headers: authHeader() }),
      ]);
      setDigest(d.data);
      setItems(i.data.items || []);
      setPolicies(p.data.items || []);
      setSubprocessors(s.data || { items: [], tracked: [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const runPulse = async () => {
    setRunningPulse(true);
    try {
      await axios.post(`${API}/api/admin/compliance/run-pulse`, {}, { headers: authHeader() });
      toast.success('Pulse started. Refresh in ~60s to see new items.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start pulse');
    } finally {
      setRunningPulse(false);
    }
  };

  const runSubprocScan = async () => {
    setRunningScan(true);
    try {
      await axios.post(`${API}/api/admin/compliance/run-subproc-scan`, {}, { headers: authHeader() });
      toast.success('Sub-processor scan started. Results appear in ~30s.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start scan');
    } finally {
      setRunningScan(false);
    }
  };

  const dismissItem = async (itemId) => {
    try {
      await axios.post(`${API}/api/admin/compliance/items/${itemId}/dismiss`, {}, { headers: authHeader() });
      setItems((arr) => arr.filter((it) => it.item_id !== itemId));
      toast.success('Item dismissed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to dismiss');
    }
  };

  const markReviewed = async (policyKey) => {
    try {
      await axios.post(`${API}/api/admin/compliance/policies/${policyKey}/mark-reviewed`, {}, { headers: authHeader() });
      toast.success('Policy marked reviewed');
      const p = await axios.get(`${API}/api/admin/compliance/policies`, { headers: authHeader() });
      setPolicies(p.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark reviewed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" />
      </div>
    );
  }

  const totals = digest?.totals || {};

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-compliance-page">
      <header className="border-b border-white/10 sticky top-0 bg-black/95 backdrop-blur z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-neutral-400 hover:text-white inline-flex items-center gap-1 text-sm">
              <ChevronLeft className="w-4 h-4" /> Admin
            </Link>
            <span className="text-neutral-700">|</span>
            <h1 className="font-bold text-lg inline-flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#00F0FF]" />
              Compliance Watch
            </h1>
          </div>
          <div className="flex gap-2">
            <button onClick={runPulse} disabled={runningPulse}
                    data-testid="run-pulse-btn"
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-white/5 border border-white/10 hover:border-[#00F0FF]/40 rounded-lg transition disabled:opacity-50">
              {runningPulse ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Run pulse now
            </button>
            <button onClick={runSubprocScan} disabled={runningScan}
                    data-testid="run-subproc-btn"
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-white/5 border border-white/10 hover:border-[#00F0FF]/40 rounded-lg transition disabled:opacity-50">
              {runningScan ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Building2 className="w-3.5 h-3.5" />}
              Scan vendors
            </button>
          </div>
        </div>
      </header>

      {/* Summary cards */}
      <section className="max-w-6xl mx-auto px-6 py-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="compliance-totals">
          <SummaryCard label="Action required" value={totals.action_required ?? 0} accent="#dc2626" />
          <SummaryCard label="Monitor" value={totals.monitor ?? 0} accent="#f59e0b" />
          <SummaryCard label="Policies needing review" value={(totals.policies_stale ?? 0) + (totals.policies_soon ?? 0)} accent="#00F0FF" />
          <SummaryCard label="Vendor changes (30d)" value={totals.subprocessor_changes ?? 0} accent="#a78bfa" />
        </div>
      </section>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-6 border-b border-white/10">
        <div className="flex gap-1">
          {['digest', 'items', 'policies', 'vendors'].map((t) => (
            <button key={t} onClick={() => setTab(t)}
                    data-testid={`tab-${t}`}
                    className={`px-4 py-2.5 text-sm capitalize transition border-b-2 ${
                      tab === t
                        ? 'text-white border-[#00F0FF]'
                        : 'text-neutral-500 border-transparent hover:text-neutral-300'
                    }`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      <section className="max-w-6xl mx-auto px-6 py-6">
        {tab === 'digest' && <DigestView digest={digest} onDismiss={dismissItem} onMarkReviewed={markReviewed} />}
        {tab === 'items' && <ItemsView items={items} onDismiss={dismissItem} />}
        {tab === 'policies' && <PoliciesView policies={policies} onMarkReviewed={markReviewed} />}
        {tab === 'vendors' && <VendorsView subprocessors={subprocessors} />}
      </section>
    </div>
  );
}

function SummaryCard({ label, value, accent }) {
  return (
    <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-4">
      <div className="text-xs uppercase text-neutral-500 tracking-wide mb-1">{label}</div>
      <div className="text-3xl font-bold" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function DigestView({ digest, onDismiss, onMarkReviewed }) {
  if (!digest) return <p className="text-neutral-500">No digest yet.</p>;
  const action = (digest.new_items || []).filter((i) => i.relevance === 'action_required');
  const monitor = (digest.new_items || []).filter((i) => i.relevance === 'monitor');
  const stale = (digest.stale_or_soon_policies || []).filter((p) => p.status === 'stale');
  const soon = (digest.stale_or_soon_policies || []).filter((p) => p.status === 'soon');
  const vendorChanges = digest.subprocessor_changes || [];

  return (
    <div className="space-y-8">
      {action.length > 0 && (
        <div>
          <h2 className="text-sm font-bold uppercase tracking-wide text-red-400 mb-3">Action required</h2>
          <div className="space-y-2">
            {action.map((it) => <ItemCard key={it.item_id} item={it} onDismiss={onDismiss} accent="red" />)}
          </div>
        </div>
      )}
      {monitor.length > 0 && (
        <div>
          <h2 className="text-sm font-bold uppercase tracking-wide text-amber-400 mb-3">Monitor</h2>
          <div className="space-y-2">
            {monitor.map((it) => <ItemCard key={it.item_id} item={it} onDismiss={onDismiss} accent="amber" />)}
          </div>
        </div>
      )}
      {(stale.length > 0 || soon.length > 0) && (
        <div>
          <h2 className="text-sm font-bold uppercase tracking-wide text-[#00F0FF] mb-3">Policies needing review</h2>
          <div className="space-y-2">
            {[...stale, ...soon].map((p) => <PolicyRow key={p.policy_key} policy={p} onMarkReviewed={onMarkReviewed} />)}
          </div>
        </div>
      )}
      {vendorChanges.length > 0 && (
        <div>
          <h2 className="text-sm font-bold uppercase tracking-wide text-purple-300 mb-3">Vendor sub-processor changes</h2>
          <div className="space-y-2">
            {vendorChanges.map((v, idx) => <VendorChangeRow key={`${v.vendor_key}-${idx}`} change={v} />)}
          </div>
        </div>
      )}
      {action.length === 0 && monitor.length === 0 && stale.length === 0
       && soon.length === 0 && vendorChanges.length === 0 && (
        <div className="text-center py-16 text-neutral-500">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <p className="text-lg font-semibold text-white">Nothing flagged this week</p>
          <p className="text-sm mt-1">Compliance posture is stable. Next pulse: Monday 06:00 PT.</p>
        </div>
      )}
    </div>
  );
}

function ItemsView({ items, onDismiss }) {
  if (items.length === 0) {
    return <p className="text-neutral-500 text-center py-12">No items yet — run the pulse to populate.</p>;
  }
  return (
    <div className="space-y-2">
      {items.map((it) => {
        const accent = it.relevance === 'action_required' ? 'red'
                     : it.relevance === 'monitor' ? 'amber' : 'neutral';
        return <ItemCard key={it.item_id} item={it} onDismiss={onDismiss} accent={accent} />;
      })}
    </div>
  );
}

function PoliciesView({ policies, onMarkReviewed }) {
  return (
    <div className="space-y-2" data-testid="policies-list">
      {policies.map((p) => <PolicyRow key={p.policy_key} policy={p} onMarkReviewed={onMarkReviewed} />)}
    </div>
  );
}

function VendorsView({ subprocessors }) {
  const latestByVendor = {};
  (subprocessors.items || []).forEach((s) => {
    if (!latestByVendor[s.vendor_key]) latestByVendor[s.vendor_key] = s;
  });

  return (
    <div className="space-y-2">
      {(subprocessors.tracked || []).map((t) => {
        const snap = latestByVendor[t.vendor_key];
        return (
          <div key={t.vendor_key} className="bg-neutral-900/40 border border-white/10 rounded-xl p-4 flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="font-semibold">{t.vendor_name}</div>
              <a href={t.url} target="_blank" rel="noopener noreferrer"
                 className="text-xs text-neutral-500 hover:text-[#00F0FF] inline-flex items-center gap-1 mt-0.5">
                {t.url}
                <ExternalLink className="w-3 h-3" />
              </a>
              {snap?.change_summary && (
                <div className="text-sm text-neutral-300 mt-2 leading-relaxed bg-purple-500/5 border-l-2 border-purple-400 pl-3 py-2">
                  <span className="text-xs uppercase tracking-wide text-purple-300 font-semibold">Last change · {new Date(snap.created_at).toLocaleDateString()}</span>
                  <div className="mt-1">{snap.change_summary}</div>
                </div>
              )}
              {!snap && (
                <div className="text-xs text-neutral-600 mt-1">No snapshot yet — run a vendor scan.</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ItemCard({ item, onDismiss, accent }) {
  const colorMap = {
    red: { border: 'border-red-500/40', badgeBg: 'bg-red-500/10 text-red-400', label: 'ACTION REQUIRED' },
    amber: { border: 'border-amber-500/30', badgeBg: 'bg-amber-500/10 text-amber-400', label: 'MONITOR' },
    neutral: { border: 'border-white/10', badgeBg: 'bg-neutral-500/10 text-neutral-400', label: 'NOT RELEVANT' },
  };
  const c = colorMap[accent] || colorMap.neutral;
  return (
    <div className={`bg-neutral-900/40 border ${c.border} rounded-xl p-4`} data-testid="compliance-item">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 mt-0.5 text-neutral-400" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide font-bold ${c.badgeBg}`}>{c.label}</span>
            <span className="text-xs text-neutral-500">{item.source_label} · {item.jurisdiction}</span>
            {(item.tags || []).map((t) => (
              <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-neutral-400">{t}</span>
            ))}
          </div>
          <div className="font-semibold leading-snug">{item.title}</div>
          {item.one_line && item.one_line !== '—' && (
            <p className="text-sm text-neutral-300 mt-1.5 leading-relaxed">{item.one_line}</p>
          )}
          {item.suggested_action && (
            <p className="text-sm text-[#00F0FF] mt-2 leading-relaxed">
              <span className="font-semibold">Action:</span> {item.suggested_action}
            </p>
          )}
          <div className="flex items-center gap-3 mt-3 text-xs">
            {item.link && (
              <a href={item.link} target="_blank" rel="noopener noreferrer"
                 className="text-neutral-400 hover:text-[#00F0FF] inline-flex items-center gap-1">
                Read source <ExternalLink className="w-3 h-3" />
              </a>
            )}
            <button onClick={() => onDismiss(item.item_id)}
                    data-testid="dismiss-item-btn"
                    className="text-neutral-500 hover:text-white">
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PolicyRow({ policy, onMarkReviewed }) {
  const statusColor = {
    stale: 'text-red-400 bg-red-500/10',
    soon: 'text-amber-400 bg-amber-500/10',
    fresh: 'text-emerald-400 bg-emerald-500/10',
  }[policy.status] || 'text-neutral-400 bg-white/5';
  const dueLabel = policy.days_until_due < 0
    ? `${Math.abs(policy.days_until_due)}d overdue`
    : `${policy.days_until_due}d`;
  return (
    <div className="bg-neutral-900/40 border border-white/10 rounded-xl p-4 flex items-center justify-between gap-3" data-testid="policy-row">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <FileText className="w-5 h-5 text-neutral-400" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold">{policy.label}</div>
          <Link to={policy.path} className="text-xs text-neutral-500 hover:text-[#00F0FF]">{policy.path}</Link>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className={`text-xs px-2 py-1 rounded-full uppercase tracking-wide font-bold ${statusColor}`}>
          {policy.status} &middot; {dueLabel}
        </span>
        <button onClick={() => onMarkReviewed(policy.policy_key)}
                data-testid={`mark-reviewed-${policy.policy_key}`}
                className="text-xs px-3 py-1.5 bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/40 rounded-lg hover:bg-[#00F0FF]/20 transition inline-flex items-center gap-1">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Mark reviewed
        </button>
      </div>
    </div>
  );
}

function VendorChangeRow({ change }) {
  return (
    <div className="bg-neutral-900/40 border border-purple-500/20 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <Building2 className="w-4 h-4 text-purple-300" />
        <span className="font-semibold">{change.vendor_name}</span>
        <span className="text-xs text-neutral-500 inline-flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {new Date(change.created_at).toLocaleDateString()}
        </span>
        <a href={change.url} target="_blank" rel="noopener noreferrer" className="text-xs text-neutral-500 hover:text-[#00F0FF] inline-flex items-center gap-1">
          source <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      <p className="text-sm text-neutral-300 leading-relaxed">{change.change_summary}</p>
    </div>
  );
}
